import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../services/api';

const AppContext = createContext(null);

// Request deduplication cache: prevents simultaneous duplicate requests
const requestCache = {};
const requestTimestamps = {};
const CACHE_TTL_MS = 5000; // 5 second cache

const dedupedRequest = async (key, requestFn) => {
  const now = Date.now();
  
  // Return cached result if valid
  if (requestCache[key] && requestTimestamps[key] && (now - requestTimestamps[key]) < CACHE_TTL_MS) {
    return requestCache[key];
  }
  
  // Return pending promise if request is in flight
  if (requestCache[key] && requestCache[key] instanceof Promise) {
    return requestCache[key];
  }
  
  // Execute request and cache it
  const promise = requestFn().then(data => {
    requestCache[key] = data;
    requestTimestamps[key] = now;
    return data;
  }).catch(err => {
    delete requestCache[key];
    throw err;
  });
  
  requestCache[key] = promise;
  return promise;
};

// Granular project templates
export const projectTemplates = {
  fullstack: {
    name: 'Full Stack',
    categories: {
      'discovery': [
        { name: 'Initial Consultation', rate: 150 }, { name: 'Requirements Document', rate: 400 }, { name: 'Technical Feasibility Study', rate: 500 }, { name: 'Project Timeline', rate: 250 },
      ],
      'design': [
        { name: 'User Research', rate: 400 }, { name: 'Low-Fi Wireframes', rate: 500 }, { name: 'High-Fi Wireframes', rate: 800 }, { name: 'Interactive Prototype', rate: 1000 },
        { name: 'Design System', rate: 1200 }, { name: 'Mobile Mockups', rate: 600 }, { name: 'Dark Mode Design', rate: 500 }, { name: 'Accessibility Audit', rate: 400 },
      ],
      'backend': [
        { name: 'Server Setup (Node/Express)', rate: 400 }, { name: 'Database Setup (MongoDB)', rate: 300 }, { name: 'Database Setup (PostgreSQL)', rate: 350 },
        { name: 'Auth (Email/Password)', rate: 500 }, { name: 'OAuth (Google)', rate: 350 }, { name: 'OAuth (Facebook)', rate: 350 }, { name: 'Two-Factor Auth', rate: 450 },
        { name: 'Password Reset Flow', rate: 250 }, { name: 'Role-Based Access Control', rate: 500 }, { name: 'JWT Token Management', rate: 300 },
        { name: 'RESTful API (per resource)', rate: 300 }, { name: 'GraphQL Setup', rate: 800 }, { name: 'WebSocket Server', rate: 600 },
        { name: 'File Upload (Local)', rate: 300 }, { name: 'File Upload (S3)', rate: 450 }, { name: 'Image Processing', rate: 350 },
        { name: 'PDF Generation', rate: 400 }, { name: 'CSV Export', rate: 300 }, { name: 'Background Jobs (Redis)', rate: 450 },
        { name: 'Email Service (SendGrid)', rate: 350 }, { name: 'SMS (Twilio)', rate: 400 }, { name: 'Push Notifications', rate: 450 },
        { name: 'Search (Basic)', rate: 300 }, { name: 'Search (Elasticsearch)', rate: 700 }, { name: 'Caching (Redis)', rate: 400 },
        { name: 'API Documentation', rate: 400 }, { name: 'Unit Tests', rate: 200 }, { name: 'Integration Tests', rate: 350 },
      ],
      'frontend': [
        { name: 'React Setup', rate: 300 }, { name: 'Next.js Setup', rate: 400 }, { name: 'TypeScript Config', rate: 200 },
        { name: 'State Management (Redux)', rate: 400 }, { name: 'State Management (Zustand)', rate: 300 }, { name: 'Routing', rate: 200 },
        { name: 'Login Page', rate: 300 }, { name: 'Registration Page', rate: 300 }, { name: 'Profile Page', rate: 350 },
        { name: 'Navigation Header', rate: 250 }, { name: 'Sidebar', rate: 300 }, { name: 'Footer', rate: 150 },
        { name: 'Data Table', rate: 400 }, { name: 'Table Sorting/Filtering', rate: 200 }, { name: 'Table Pagination', rate: 150 },
        { name: 'Search with Autocomplete', rate: 350 }, { name: 'Modal Component', rate: 200 }, { name: 'Dropdown Menu', rate: 150 },
        { name: 'Tabs Component', rate: 150 }, { name: 'Accordion Component', rate: 150 }, { name: 'Carousel/Slider', rate: 300 },
        { name: 'Image Gallery', rate: 350 }, { name: 'File Upload (Drag & Drop)', rate: 350 }, { name: 'Rich Text Editor', rate: 400 },
        { name: 'Date Picker', rate: 200 }, { name: 'Calendar Component', rate: 400 }, { name: 'Form Validation', rate: 250 },
        { name: 'Multi-step Form', rate: 450 }, { name: 'Toast Notifications', rate: 200 }, { name: 'Loading Skeletons', rate: 200 },
        { name: '404 Page', rate: 200 }, { name: 'Infinite Scroll', rate: 300 }, { name: 'Lazy Loading', rate: 250 },
        { name: 'Page Animations', rate: 350 }, { name: 'Dark Mode Toggle', rate: 300 }, { name: 'i18n Setup', rate: 400 },
        { name: 'Performance Optimization', rate: 400 }, { name: 'E2E Tests (Cypress)', rate: 500 },
      ],
      'integrations': [
        { name: 'Stripe Payments', rate: 600 }, { name: 'PayPal', rate: 500 }, { name: 'Apple Pay', rate: 450 }, { name: 'Subscriptions/Billing', rate: 700 },
        { name: 'Google Analytics', rate: 250 }, { name: 'Facebook Pixel', rate: 200 }, { name: 'Hotjar', rate: 200 },
        { name: 'HubSpot CRM', rate: 500 }, { name: 'Salesforce', rate: 800 }, { name: 'Mailchimp', rate: 350 },
        { name: 'Intercom Chat', rate: 350 }, { name: 'Slack Integration', rate: 400 }, { name: 'Zapier', rate: 450 },
        { name: 'Google Maps', rate: 350 }, { name: 'Calendar (Google)', rate: 400 }, { name: 'Social Sharing', rate: 200 },
        { name: 'OpenAI/ChatGPT', rate: 600 }, { name: 'Custom API (each)', rate: 450 }, { name: 'Webhook Setup', rate: 300 },
      ],
      'devops': [
        { name: 'Git Setup', rate: 100 }, { name: 'CI/CD Pipeline', rate: 400 }, { name: 'Docker Config', rate: 400 },
        { name: 'AWS Deployment', rate: 400 }, { name: 'Vercel Deployment', rate: 250 }, { name: 'Render Deployment', rate: 250 },
        { name: 'Domain Config', rate: 150 }, { name: 'SSL Setup', rate: 150 }, { name: 'Database Backups', rate: 300 },
        { name: 'Monitoring (DataDog)', rate: 400 }, { name: 'Uptime Monitoring', rate: 200 }, { name: 'Log Aggregation', rate: 350 },
      ],
      'seo': [
        { name: 'Meta Tags', rate: 200 }, { name: 'Open Graph Tags', rate: 150 }, { name: 'Structured Data', rate: 300 },
        { name: 'Sitemap', rate: 150 }, { name: 'Robots.txt', rate: 100 }, { name: 'Page Speed Optimization', rate: 400 },
        { name: 'Core Web Vitals', rate: 500 }, { name: 'SEO Audit', rate: 350 },
      ],
      'documentation': [
        { name: 'Technical Docs', rate: 500 }, { name: 'API Documentation', rate: 400 }, { name: 'User Guide', rate: 400 },
        { name: 'Video Tutorial (each)', rate: 300 }, { name: 'Training Session (hour)', rate: 150 }, { name: 'README & Setup', rate: 200 },
      ],
    },
  },
  ecommerce: {
    name: 'E-commerce',
    categories: {
      'storefront': [
        { name: 'Homepage Design', rate: 600 }, { name: 'Category Page', rate: 400 }, { name: 'Product Listing', rate: 400 },
        { name: 'Product Detail Page', rate: 500 }, { name: 'Product Zoom', rate: 200 }, { name: 'Product Reviews', rate: 450 },
        { name: 'Search Results', rate: 350 }, { name: 'Wishlist Page', rate: 250 },
      ],
      'cart & checkout': [
        { name: 'Shopping Cart', rate: 500 }, { name: 'Mini Cart', rate: 300 }, { name: 'Guest Checkout', rate: 350 },
        { name: 'Multi-step Checkout', rate: 500 }, { name: 'Address Autocomplete', rate: 300 }, { name: 'Shipping Selection', rate: 350 },
        { name: 'Promo Codes', rate: 300 }, { name: 'Cart Recovery Emails', rate: 400 },
      ],
      'products': [
        { name: 'Product CRUD', rate: 500 }, { name: 'Categories System', rate: 350 }, { name: 'Product Variants', rate: 500 },
        { name: 'Product Images', rate: 350 }, { name: 'Related Products', rate: 400 }, { name: 'Product Import (CSV)', rate: 350 },
      ],
      'payments': [
        { name: 'Stripe', rate: 500 }, { name: 'PayPal', rate: 400 }, { name: 'Apple Pay', rate: 400 },
        { name: 'Afterpay/Klarna', rate: 450 }, { name: 'Refund Processing', rate: 300 },
      ],
      'shipping': [
        { name: 'Shipping Zones', rate: 300 }, { name: 'Real-time Rates', rate: 450 }, { name: 'UPS Integration', rate: 400 },
        { name: 'FedEx Integration', rate: 400 }, { name: 'Tracking Integration', rate: 300 }, { name: 'Returns System', rate: 500 },
      ],
      'inventory': [
        { name: 'Stock Tracking', rate: 450 }, { name: 'Low Stock Alerts', rate: 250 }, { name: 'Back-in-Stock Notifications', rate: 300 },
        { name: 'Order Management', rate: 500 }, { name: 'Invoice Generation', rate: 350 },
      ],
      'marketing': [
        { name: 'Coupon System', rate: 400 }, { name: 'Flash Sales', rate: 400 }, { name: 'Loyalty Points', rate: 600 },
        { name: 'Referral Program', rate: 700 }, { name: 'Gift Cards', rate: 500 }, { name: 'Email Popups', rate: 250 },
      ],
    },
  },
  website: {
    name: 'Website',
    categories: {
      'pages': [
        { name: 'Homepage', rate: 800 }, { name: 'About Page', rate: 400 }, { name: 'Services Page', rate: 450 },
        { name: 'Portfolio Page', rate: 500 }, { name: 'Team Page', rate: 350 }, { name: 'Contact Page', rate: 300 },
        { name: 'Pricing Page', rate: 400 }, { name: 'FAQ Page', rate: 300 }, { name: 'Landing Page', rate: 600 },
        { name: 'Privacy Policy', rate: 200 }, { name: 'Terms of Service', rate: 200 }, { name: '404 Page', rate: 200 },
      ],
      'blog': [
        { name: 'Blog Setup', rate: 500 }, { name: 'Blog Archive', rate: 350 }, { name: 'Post Template', rate: 400 },
        { name: 'Categories/Tags', rate: 250 }, { name: 'Blog Comments', rate: 350 }, { name: 'RSS Feed', rate: 150 },
      ],
      'forms': [
        { name: 'Contact Form', rate: 250 }, { name: 'Quote Request', rate: 350 }, { name: 'Newsletter Signup', rate: 200 },
        { name: 'Multi-step Form', rate: 500 }, { name: 'File Upload Form', rate: 350 }, { name: 'Spam Protection', rate: 200 },
      ],
      'interactive': [
        { name: 'Image Slider', rate: 300 }, { name: 'Video Background', rate: 350 }, { name: 'Parallax Effect', rate: 300 },
        { name: 'Animated Counters', rate: 200 }, { name: 'Testimonial Slider', rate: 300 }, { name: 'Logo Carousel', rate: 250 },
        { name: 'Filterable Gallery', rate: 400 }, { name: 'Interactive Map', rate: 350 }, { name: 'Live Chat Widget', rate: 300 },
      ],
    },
  },
  custom: {
    name: 'Custom',
    categories: {
      'consulting': [
        { name: 'Tech Consultation (hour)', rate: 150 }, { name: 'Code Review', rate: 200 }, { name: 'Architecture Review', rate: 350 },
        { name: 'Security Audit', rate: 500 }, { name: 'Performance Audit', rate: 400 },
      ],
      'mobile': [
        { name: 'React Native Setup', rate: 500 }, { name: 'App Store Submission', rate: 300 }, { name: 'Push Notifications', rate: 400 },
      ],
      'ai': [
        { name: 'OpenAI Integration', rate: 600 }, { name: 'Chatbot Development', rate: 800 }, { name: 'Content Generation', rate: 700 },
      ],
      'misc': [
        { name: 'Logo Design', rate: 500 }, { name: 'Email Template', rate: 350 }, { name: 'Domain Setup', rate: 100 },
        { name: 'SSL Setup', rate: 150 }, { name: 'Bug Fixes (hour)', rate: 100 }, { name: 'Feature Enhancement (hour)', rate: 125 },
      ],
    },
  },
};

export function AppProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [teamPayouts, setTeamPayouts] = useState([]);
  const [taxRemittances, setTaxRemittances] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [financialReminders, setFinancialReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState({ messagesUnread: 0, documentsNew: 0, documentsTotal: 0, reviewRequestsUnread: 0, reviewsPending: 0 });
  const [proposalContentTemplates, setProposalContentTemplates] = useState([]);
  const [templates, setTemplates] = useState(() => {
    try {
      const saved = localStorage.getItem('projectTemplates');
      return saved ? JSON.parse(saved) : projectTemplates;
    } catch (e) {
      return projectTemplates;
    }
  });

  const fetchDocuments = useCallback(async (filters = {}) => { try { setDocuments(await dedupedRequest('documents:' + JSON.stringify(filters), () => api.getDocuments(filters))); } catch (err) { setError('Failed to fetch documents'); } }, []);
  const removeDocument = async (docId) => { await api.deleteDocument(docId); delete requestCache['documents:' + JSON.stringify({})]; setDocuments(prev => prev.filter(d => d._id !== docId)); };
  const restoreDocument = async (docId) => {
    const d = await api.restoreDocument(docId);
    delete requestCache['documents:' + JSON.stringify({})];
    setDocuments(prev => prev.map(x => x._id === docId ? d : x));
    return d;
  };
  const patchDocument = async (docId, data) => { const d = await api.patchDocument(docId, data); delete requestCache['documents:' + JSON.stringify({})]; setDocuments(prev => prev.map(x => x._id === docId ? d : x)); return d; };
  const uploadClientDocument = async (clientId, formData) => { const d = await api.uploadClientDocument(clientId, formData); delete requestCache['documents:' + JSON.stringify({ client: clientId })]; return d; };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const savedUser = localStorage.getItem('adminUser');
    if (token && savedUser) {
      try { setUser(JSON.parse(savedUser)); setIsAuthenticated(true); } 
      catch (e) { localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser'); }
    }
  }, []);

  const login = async (email, password) => {
    setLoading(true); setError(null);
    try {
      const data = await api.adminLogin(email, password);
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      setUser(data.user); setIsAuthenticated(true);
      return data;
    } catch (err) { setError(err.response?.data?.error || 'Login failed'); throw err; }
    finally { setLoading(false); }
  };

  const acceptStaffInvite = async (email, inviteToken, password) => {
    setLoading(true); setError(null);
    try {
      const data = await api.acceptStaffInvite(email, inviteToken, password);
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      setUser(data.user); setIsAuthenticated(true);
      return data;
    } catch (err) { setError(err.response?.data?.error || 'Invite acceptance failed'); throw err; }
    finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser');
    setUser(null); setIsAuthenticated(false);
  };

  const changeAdminPassword = async (currentPassword, newPassword) => {
    setLoading(true);
    try {
      const r = await api.changeAdminPassword(currentPassword, newPassword);
      return r;
    } catch (err) {
      setError(err.response?.data?.error || 'Change password failed');
      throw err;
    } finally { setLoading(false); }
  }; 

  const updateAdminProfile = async (data) => {
    setLoading(true);
    try {
      const r = await api.updateAdminProfile(data);
      const nextUser = { ...(user || {}), ...(r.user || {}) };
      setUser(nextUser);
      localStorage.setItem('adminUser', JSON.stringify(nextUser));
      return r;
    } catch (err) {
      setError(err.response?.data?.error || 'Profile update failed');
      throw err;
    } finally { setLoading(false); }
  };

  const fetchClients = useCallback(async () => { try { setClients(await dedupedRequest('clients', api.getClients)); } catch (err) { setError('Failed to fetch clients'); } }, []);
  const addClient = async (data) => { const c = await api.createClient(data); delete requestCache['clients']; setClients(prev => [c, ...prev]); return c; };
  const editClient = async (id, data) => { const c = await api.updateClient(id, data); delete requestCache['clients']; setClients(prev => prev.map(x => x._id === id ? c : x)); return c; };
  const removeClient = async (id) => { await api.deleteClient(id); delete requestCache['clients']; setClients(prev => prev.filter(x => x._id !== id)); };

  const inviteClient = async (id, expiresInHours = 168) => {
    const r = await api.createInvite(id, expiresInHours);
    delete requestCache['clients'];
    await fetchClients();
    return r;
  };

  const fetchInvoices = useCallback(async () => { try { setInvoices(await dedupedRequest('invoices', api.getInvoices)); } catch (err) { setError('Failed to fetch invoices'); } }, []);
  const addInvoice = async (data) => { const i = await api.createInvoice(data); delete requestCache['invoices']; setInvoices(prev => [i, ...prev]); return i; };
  const editInvoice = async (id, data) => { const i = await api.updateInvoice(id, data); delete requestCache['invoices']; setInvoices(prev => prev.map(x => x._id === id ? i : x)); return i; };
  const reviseInvoice = async (id, data) => {
    const r = await api.reviseInvoice(id, data);
    delete requestCache['invoices'];
    const invoice = r?.invoice || r;
    setInvoices(prev => [invoice, ...prev]);
    return r;
  };
  const changeInvoiceStatus = async (id, status) => { const i = await api.updateInvoiceStatus(id, status); delete requestCache['invoices']; setInvoices(prev => prev.map(x => x._id === id ? i : x)); return i; };
  const mailInvoice = async (id, customHtml = undefined, includePayLink = false) => { const r = await api.sendInvoice(id, { customHtml, includePayLink }); delete requestCache['invoices']; setInvoices(prev => prev.map(x => x._id === id ? r.invoice : x)); return r; };
  const removeInvoice = async (id) => { await api.deleteInvoice(id); delete requestCache['invoices']; setInvoices(prev => prev.filter(x => x._id !== id)); };

  const fetchLeads = useCallback(async () => { try { setLeads(await dedupedRequest('leads', api.getLeads)); } catch (err) { setError('Failed to fetch leads'); } }, []);
  const changeLeadStatus = async (id, statusOrPayload) => { const l = await api.updateLeadStatus(id, statusOrPayload); delete requestCache['leads']; setLeads(prev => prev.map(x => x._id === id ? l : x)); return l; };
  const removeLead = async (id) => { await api.deleteLead(id); delete requestCache['leads']; setLeads(prev => prev.filter(x => x._id !== id)); };
  const scanLeads = async (location, industry, options = {}) => { setLoading(true); try { const r = await api.scanForLeads(location, industry, options); delete requestCache['leads']; setLeads(prev => [...(r?.leads || []), ...prev]); return r; } finally { setLoading(false); } };

  const fetchSettings = useCallback(async () => { try { setSettings(await dedupedRequest('settings', api.getSettings)); } catch (err) { setError('Failed to fetch settings'); } }, []);
  const saveSettings = async (data) => { const s = await api.updateSettings(data); delete requestCache['settings']; setSettings(s); return s; };

  const fetchDashboard = useCallback(async () => { try { setDashboardStats(await dedupedRequest('dashboard', api.getDashboardStats)); } catch (err) { setError('Failed to fetch stats'); } }, []);
  const fetchFinancialSummary = useCallback(async (params = {}) => {
    try {
      const key = 'financial-summary:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getFinancialSummary(params));
      setFinancialSummary(data);
      return data;
    } catch (err) {
      setError('Failed to fetch financial summary');
      return null;
    }
  }, []);
  const fetchExpenses = useCallback(async (params = {}) => {
    try {
      const key = 'financial-expenses:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getExpenses(params));
      setExpenses(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch expenses');
      return [];
    }
  }, []);
  const addExpense = async (data) => {
    const created = await api.createExpense(data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-expenses:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setExpenses((prev) => [created, ...prev]);
    return created;
  };
  const removeExpense = async (id) => {
    await api.deleteExpense(id);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-expenses:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setExpenses((prev) => prev.filter((x) => x._id !== id));
  };
  const fetchTeamPayouts = useCallback(async (params = {}) => {
    try {
      const key = 'financial-payouts:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getTeamPayouts(params));
      setTeamPayouts(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch team payouts');
      return [];
    }
  }, []);
  const addTeamPayout = async (data) => {
    const created = await api.createTeamPayout(data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-payouts:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setTeamPayouts((prev) => [created, ...prev]);
    return created;
  };
  const removeTeamPayout = async (id) => {
    await api.deleteTeamPayout(id);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-payouts:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setTeamPayouts((prev) => prev.filter((x) => x._id !== id));
  };
  const changeTeamPayoutStatus = async (id, status) => {
    const updated = await api.updateTeamPayoutStatus(id, status);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-payouts:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setTeamPayouts((prev) => prev.map((x) => (x._id === id ? updated : x)));
    return updated;
  };
  const fetchTaxRemittances = useCallback(async (params = {}) => {
    try {
      const key = 'financial-tax-remits:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getTaxRemittances(params));
      setTaxRemittances(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch tax remittances');
      return [];
    }
  }, []);
  const addTaxRemittance = async (data) => {
    const created = await api.createTaxRemittance(data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-tax-remits:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setTaxRemittances((prev) => [created, ...prev]);
    return created;
  };
  const removeTaxRemittance = async (id) => {
    await api.deleteTaxRemittance(id);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-tax-remits:') || k.startsWith('financial-summary:')) delete requestCache[k]; });
    setTaxRemittances((prev) => prev.filter((x) => x._id !== id));
  };
  const fetchTimeEntries = useCallback(async (params = {}) => {
    try {
      const key = 'financial-time-entries:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getTimeEntries(params));
      setTimeEntries(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch time entries');
      return [];
    }
  }, []);
  const addTimeEntry = async (data) => {
    const created = await api.createTimeEntry(data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-time-entries:')) delete requestCache[k]; });
    setTimeEntries((prev) => [created, ...prev]);
    return created;
  };
  const editTimeEntry = async (id, data) => {
    const updated = await api.updateTimeEntry(id, data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-time-entries:')) delete requestCache[k]; });
    setTimeEntries((prev) => prev.map((x) => (x._id === id ? updated : x)));
    return updated;
  };
  const removeTimeEntry = async (id) => {
    await api.deleteTimeEntry(id);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-time-entries:')) delete requestCache[k]; });
    setTimeEntries((prev) => prev.filter((x) => x._id !== id));
  };
  const generateAutoDraftPayouts = async (data = {}) => {
    const result = await api.autoDraftTeamPayouts(data);
    Object.keys(requestCache).forEach((k) => {
      if (k.startsWith('financial-payouts:') || k.startsWith('financial-time-entries:') || k.startsWith('financial-summary:')) delete requestCache[k];
    });
    return result;
  };
  const runStripeFeeSync = async (data = {}) => {
    const result = await api.syncStripeFeeExpenses(data);
    Object.keys(requestCache).forEach((k) => {
      if (k.startsWith('financial-expenses:') || k.startsWith('financial-summary:')) delete requestCache[k];
    });
    return result;
  };
  const fetchFinancialReminders = useCallback(async (params = {}) => {
    try {
      const key = 'financial-reminders:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getFinancialReminders(params));
      setFinancialReminders(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch reminders');
      return [];
    }
  }, []);
  const runMonthlyReminderCycle = async () => {
    const result = await api.runMonthlyFinancialReminders();
    Object.keys(requestCache).forEach((k) => {
      if (k.startsWith('financial-reminders:') || k.startsWith('financial-summary:')) delete requestCache[k];
    });
    return result;
  };
  const resolveReminder = async (id) => {
    const updated = await api.resolveFinancialReminder(id);
    Object.keys(requestCache).forEach((k) => {
      if (k.startsWith('financial-reminders:') || k.startsWith('financial-summary:')) delete requestCache[k];
    });
    setFinancialReminders((prev) => prev.map((x) => (x._id === id ? updated : x)));
    return updated;
  };
  const fetchContractors = useCallback(async (params = {}) => {
    try {
      const key = 'financial-contractors:' + JSON.stringify(params || {});
      const data = await dedupedRequest(key, () => api.getContractors(params));
      setContractors(data || []);
      return data || [];
    } catch (err) {
      setError('Failed to fetch contractors');
      return [];
    }
  }, []);
  const addContractor = async (data) => {
    const created = await api.createContractor(data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-contractors:')) delete requestCache[k]; });
    setContractors((prev) => [...prev, created].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    return created;
  };
  const editContractor = async (id, data) => {
    const updated = await api.updateContractor(id, data);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-contractors:')) delete requestCache[k]; });
    setContractors((prev) => prev.map((x) => (x._id === id ? updated : x)));
    return updated;
  };
  const removeContractor = async (id) => {
    await api.archiveContractor(id);
    Object.keys(requestCache).forEach((k) => { if (k.startsWith('financial-contractors:')) delete requestCache[k]; });
    setContractors((prev) => prev.map((x) => (x._id === id ? { ...x, isActive: false } : x)));
  };
  const exportFinancialCsv = async (resource, params = {}) => {
    const blob = await api.downloadFinancialCsv(resource, params);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${resource}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  const fetchNotificationSummary = useCallback(async (params = {}) => {
    try {
      const data = await api.getNotificationSummary(params);
      setNotificationSummary({
        messagesUnread: Number(data?.messagesUnread || 0),
        documentsNew: Number(data?.documentsNew || 0),
        documentsTotal: Number(data?.documentsTotal || 0),
        reviewRequestsUnread: Number(data?.reviewRequestsUnread || 0),
        reviewsPending: Number(data?.reviewsPending || 0),
      });
      return data;
    } catch (err) {
      setError('Failed to fetch notifications');
      return null;
    }
  }, []);

  const fetchProposals = async () => { try { setProposals(await dedupedRequest('proposals', api.getProposals)); } catch (err) { setError('Failed to fetch proposals'); } };
  const addProposal = async (data) => { const p = await api.createProposal(data); delete requestCache['proposals']; setProposals(prev => [p, ...prev]); return p; };
  const editProposal = async (id, data) => { const p = await api.updateProposal(id, data); delete requestCache['proposals']; setProposals(prev => prev.map(x => x._id === id ? p : x)); return p; };
  const reviseProposal = async (id, data) => {
    const r = await api.reviseProposal(id, data);
    delete requestCache['proposals'];
    const proposal = r?.proposal || r;
    setProposals(prev => [proposal, ...prev]);
    return r;
  };
  const changeProposalStatus = async (id, status) => { const p = await api.updateProposalStatus(id, status); delete requestCache['proposals']; setProposals(prev => prev.map(x => x._id === id ? p : x)); return p; };
  const sendProposal = async (id, customHtml = undefined) => { const r = await api.sendProposal(id, customHtml ? { customHtml } : {}); delete requestCache['proposals']; setProposals(prev => prev.map(x => x._id === id ? r.proposal || r : x)); return r; };
  const convertProposalToInvoice = async (id) => { const r = await api.convertProposal(id); delete requestCache['proposals']; delete requestCache['invoices']; return r; };
  const removeProposal = async (id) => { await api.deleteProposal(id); delete requestCache['proposals']; setProposals(prev => prev.filter(x => x._id !== id)); };
  const fetchProposalContentTemplates = async () => {
    try {
      setProposalContentTemplates(await dedupedRequest('proposal-content-templates', api.getProposalContentTemplates));
    } catch (err) {
      setError('Failed to fetch proposal content templates');
    }
  };
  const addProposalContentTemplate = async (data) => {
    const t = await api.createProposalContentTemplate(data);
    delete requestCache['proposal-content-templates'];
    setProposalContentTemplates((prev) => [...prev, t].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    return t;
  };
  const editProposalContentTemplate = async (id, data) => {
    const t = await api.updateProposalContentTemplate(id, data);
    delete requestCache['proposal-content-templates'];
    setProposalContentTemplates((prev) => prev.map((x) => (x._id === id ? t : x)).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    return t;
  };
  const removeProposalContentTemplate = async (id) => {
    await api.deleteProposalContentTemplate(id);
    delete requestCache['proposal-content-templates'];
    setProposalContentTemplates((prev) => prev.filter((x) => x._id !== id));
  };

  const fetchProjects = useCallback(async () => { try { setProjects(await dedupedRequest('projects', api.getProjects)); } catch (err) { setError('Failed to fetch projects'); } }, []);
  const addProject = async (data) => { const p = await api.createProject(data); delete requestCache['projects']; setProjects(prev => [p, ...prev]); return p; };
  const updateProject = async (id, data) => { const p = await api.updateProject(id, data); delete requestCache['projects']; setProjects(prev => prev.map(x => x._id === id ? p : x)); return p; };
  const removeProject = async (id) => { await api.deleteProject(id); delete requestCache['projects']; setProjects(prev => prev.filter(x => x._id !== id)); };

  const saveTemplates = (next) => {
    try { localStorage.setItem('projectTemplates', JSON.stringify(next)); } catch (e) { console.error('Failed to save templates', e); }
  };

  const addTemplate = (key, data) => {
    setTemplates(prev => { const next = { ...prev, [key]: data }; saveTemplates(next); return next; });
  };

  const updateTemplate = (key, data) => {
    setTemplates(prev => { const next = { ...prev, [key]: data }; saveTemplates(next); return next; });
  };

  const removeTemplate = (key) => {
    setTemplates(prev => { const next = { ...prev }; delete next[key]; saveTemplates(next); return next; });
  };

  return (
    <AppContext.Provider value={{
      isAuthenticated, user, login, acceptStaffInvite, logout, changeAdminPassword, updateAdminProfile, currentSection, setCurrentSection,
      clients, fetchClients, addClient, editClient, removeClient, inviteClient,
      invoices, fetchInvoices, addInvoice, editInvoice, reviseInvoice, changeInvoiceStatus, mailInvoice, removeInvoice,
      leads, fetchLeads, changeLeadStatus, removeLead, scanLeads,
      settings, fetchSettings, saveSettings,
      dashboardStats, fetchDashboard,
      financialSummary, fetchFinancialSummary,
      expenses, fetchExpenses, addExpense, removeExpense,
      teamPayouts, fetchTeamPayouts, addTeamPayout, removeTeamPayout, changeTeamPayoutStatus,
      taxRemittances, fetchTaxRemittances, addTaxRemittance, removeTaxRemittance,
      contractors, fetchContractors, addContractor, editContractor, removeContractor,
      timeEntries, fetchTimeEntries, addTimeEntry, editTimeEntry, removeTimeEntry,
      generateAutoDraftPayouts, runStripeFeeSync,
      financialReminders, fetchFinancialReminders, runMonthlyReminderCycle, resolveReminder,
      exportFinancialCsv,
      notificationSummary, fetchNotificationSummary,
      projectTemplates, loading, error, setError,
      templates, addTemplate, updateTemplate, removeTemplate,
      proposals, fetchProposals, addProposal, editProposal, reviseProposal, changeProposalStatus, sendProposal, convertProposalToInvoice, removeProposal,
      proposalContentTemplates, fetchProposalContentTemplates, addProposalContentTemplate, editProposalContentTemplate, removeProposalContentTemplate,
      projects, fetchProjects, addProject, updateProject, removeProject,
      documents, fetchDocuments, removeDocument, restoreDocument, patchDocument, uploadClientDocument,
      }}> 
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
